import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { BrandsList } from '~/components/BrandsList/BrandsList';
import useModal from '~/hooks/useModal';
import {
  selectCurrentOrganizationId,
  selectSubscription,
} from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';

import { CancelSubscriptionModal } from '../CancelSubscriptionModal/CancelSubscriptionModal';
import { SubscriptionModalTitle } from '../Subscription/Subscription.styled';

import { CURRENT_PLAN_FEATURES, LOST_FEATURES } from './ConfirmSubscriptionCancelModal.constants';
import * as S from './ConfirmSubscriptionCancelModal.styled';

import CryingFaceLogo from '~/images/crying-face.svg?react';

export const ConfirmSubscriptionCancelModal = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'confirmSubscriptionCancelModal' });
  const modal = useModal();
  const activeSubscription = useAppSelector(selectSubscription);
  const organizationId = useAppSelector(selectCurrentOrganizationId);

  const continueDowngrade = () => {
    modal.showCustom(CancelSubscriptionModal);
  };

  const cancelDowngrade = () => {
    analytics.trackEvent('cancel_downgrade_button_clicked', {
      [TRACK_EVENT_ARG_TYPE.BUTTON_TYPE]: 'keep_current_plan',
      [TRACK_EVENT_ARG_TYPE.ORGANIZATION_ID]: organizationId,
      [TRACK_EVENT_ARG_TYPE.ACCOUNT_PLAN]: activeSubscription?.type ?? 'free',
      [TRACK_EVENT_ARG_TYPE.PLAN_DOWNGRADED_TO]: 'free',
      [TRACK_EVENT_ARG_TYPE.DOWNGRADE_TYPE]: 'plan_downgrade',
    });
    modal.hide();
  };

  return (
    <S.Container>
      <S.BackdropOverrides />
      <CryingFaceLogo width={40} height={40} />
      <S.TitleContainer>
        <SubscriptionModalTitle>{t('title', 'Are you sure?')}</SubscriptionModalTitle>
        <S.Subtitle>
          {t('subtitle', "You will lose all the useful features you've got...")}
        </S.Subtitle>
      </S.TitleContainer>
      <S.FeaturesGrid>
        <S.FeaturesContainer>
          <S.FeaturesListTitle>
            {t('currentPlanTitle', 'If you keep your current plan:')}
          </S.FeaturesListTitle>
          <S.FeaturesList>
            {CURRENT_PLAN_FEATURES.map((feature) => (
              <S.FeatureListItem key={feature}>
                <S.GreenCheckIcon />
                <span>{feature}</span>
              </S.FeatureListItem>
            ))}
          </S.FeaturesList>
          <S.ChoiceButton variant="primary" onClick={cancelDowngrade}>
            {t('keepCurrentPlan', 'Keep current plan')}
          </S.ChoiceButton>
        </S.FeaturesContainer>
        <S.FeaturesContainer>
          <S.FeaturesListTitle>{t('cancelTitle', 'If you cancel:')}</S.FeaturesListTitle>
          <S.FeaturesList>
            {LOST_FEATURES.map((feature) => (
              <S.FeatureListItem key={feature}>
                <S.RedCancelIcon />
                <span>{feature}</span>
              </S.FeatureListItem>
            ))}
          </S.FeaturesList>
          {activeSubscription && (
            <S.PremiumFeaturesInfo>
              {t(
                'cancelInfo',
                'Premium features will be disabled when the current subscription ends ({{date}}).',
                { date: dayjs(activeSubscription.end).format('DD MMM YYYY') },
              )}
            </S.PremiumFeaturesInfo>
          )}
          <S.ChoiceButton variant="danger" bordered onClick={continueDowngrade}>
            {t('continueDowngrade', 'Continue downgrade')}
          </S.ChoiceButton>
        </S.FeaturesContainer>
      </S.FeaturesGrid>
      <S.BrandsInfo>{t('brandsInfo', 'BugBug is used by 300+ companies')}</S.BrandsInfo>
      <BrandsList />
    </S.Container>
  );
};
